/**
 * Service programs (bundles) on the portal's Request service page (tracker item 85): each program the company
 * offers is a card with its visit list (item, day of the program, estimated price) and an estimated total, next
 * to the individual services of the form above it. Requesting one creates a request the office approves; nothing
 * is scheduled or charged until then. Prices come for one of the customer's own properties; a login that is not
 * linked yet (or a price that cannot be worked out) shows "Priced on approval".
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as types from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { RequestFailure, localToday, ownedProperties, portalAddress } from '../../utils/portal';
import { invoiceMoney } from '../invoiceUi';
import { usePortal, usePortalApi } from './PortalContext';
import { InlineAlert } from './PortalStates';
import {
  PORTAL_PROGRAMS_PATH, PRICED_ON_APPROVAL, bundleRequestProblems, describeBundleRequestFailure, priceOrApproval, programDayLabel, programSpan,
} from './portalBundleUi';

export function PortalBundleRequests() {
  const { me, reportFailure } = usePortal();
  const api = usePortalApi();
  const linked = me.status === 'LINKED';
  const owned = ownedProperties(me.properties);
  const today = localToday();
  const [propertyId, setPropertyId] = useState(owned.length === 1 ? owned[0].id : '');
  const [startDate, setStartDate] = useState('');
  const [bundles, setBundles] = useState<types.PortalRequestableBundle[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [autoRenew, setAutoRenew] = useState<Record<string, boolean>>({});
  const [touched, setTouched] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ bundleId: string; failure: RequestFailure } | null>(null);
  const [sent, setSent] = useState<{ bundleName: string; request: types.PortalEnrollment } | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoadFailed(false);
    try {
      const list = await api.listPortalRequestableBundles({
        ...(propertyId ? { propertyId } : {}),
        ...(startDate && startDate >= today ? { startDate } : {}),
      });
      if (id !== requestId.current) return;
      setBundles(list);
      // The checkbox starts at the bundle's own default; a choice the customer already made is kept
      setAutoRenew((current) => {
        const next = { ...current };
        list.forEach((b) => { if (next[b.id] === undefined) next[b.id] = b.autoRenewDefault; });
        return next;
      });
    } catch {
      if (id === requestId.current) { setBundles(null); setLoadFailed(true); }
    }
  }, [api, propertyId, startDate, today]);

  useEffect(() => { void load(); }, [load]);

  if (sent) {
    return (
      <section aria-labelledby="programs-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" data-testid="bundle-request-sent">
        <h2 id="programs-heading" className="text-base font-bold text-gray-900">Program request sent</h2>
        <p className="mt-2 text-sm text-gray-700">
          Thank you. {me.companyName} will review your request for {sent.bundleName}, confirm the dates and schedule the visits. Nothing is booked or charged until then.
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Link to={PORTAL_PROGRAMS_PATH} className={`${primaryButtonClass} inline-flex min-h-[44px] items-center justify-center`}>View your programs</Link>
          <button type="button" className={secondaryButtonClass} onClick={() => { setSent(null); setTouched(false); void load(); }}>Request another program</button>
        </div>
      </section>
    );
  }

  if (loadFailed) {
    return (
      <section aria-labelledby="programs-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <h2 id="programs-heading" className="text-base font-bold text-gray-900">Service programs</h2>
        <p role="status" className="mt-2 text-sm text-gray-600">We could not load the service programs right now.</p>
        <button type="button" className={`${secondaryButtonClass} mt-2`} onClick={() => void load()}>Try again</button>
      </section>
    );
  }
  if (!bundles || bundles.length === 0) return null;

  const problems = bundleRequestProblems({ propertyId, startDate }, today);
  const canRequest = linked && owned.length > 0;

  const request = async (bundle: types.PortalRequestableBundle) => {
    setTouched(true);
    if (busyId || Object.keys(problems).length > 0) return;
    setBusyId(bundle.id);
    setFailure(null);
    try {
      const created = await api.createPortalBundleRequest({ bundleId: bundle.id, propertyId, startDate, autoRenew: autoRenew[bundle.id] ?? bundle.autoRenewDefault });
      setSent({ bundleName: bundle.name, request: created });
    } catch (cause) {
      const described = describeBundleRequestFailure(cause, { company: me.companyName });
      if (described.kind === 'session') reportFailure({ kind: 'session', message: described.message });
      setFailure({ bundleId: bundle.id, failure: described });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section aria-labelledby="programs-heading" className="space-y-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" data-testid="bundle-requests">
      <div>
        <h2 id="programs-heading" className="text-base font-bold text-gray-900">Service programs</h2>
        <p className="mt-1 text-sm text-gray-600">
          A program is a set of visits planned together, for example a spring, summer and fall lawn plan. This is a request: {me.companyName} reviews it and confirms the dates.
        </p>
      </div>

      {canRequest ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="bundle-property" className={labelClass}>Property</label>
            <select
              id="bundle-property" className={`${inputClass} min-h-[44px]`} value={propertyId} onChange={(e) => setPropertyId(e.target.value)} disabled={!!busyId}
              aria-invalid={touched && !!problems.propertyId}
            >
              {owned.length > 1 && <option value="">Choose a property</option>}
              {owned.map((p) => <option key={p.id} value={p.id}>{portalAddress(p)}</option>)}
            </select>
            {touched && problems.propertyId && <p className="mt-1 text-xs text-red-700">{problems.propertyId}</p>}
          </div>
          <div>
            <label htmlFor="bundle-start" className={labelClass}>Start date</label>
            <input
              id="bundle-start" type="date" className={`${inputClass} min-h-[44px]`} min={today} value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!!busyId}
              aria-invalid={touched && !!problems.startDate}
            />
            {touched && problems.startDate && <p className="mt-1 text-xs text-red-700">{problems.startDate}</p>}
          </div>
        </div>
      ) : (
        <p className="rounded-md bg-blue-50 p-3 text-sm text-blue-900" data-testid="bundle-unlinked-note">
          {linked
            ? 'We do not have a property of yours on file yet, so please call the office to ask for a program.'
            : 'Your account is still being matched to your customer record. You can see the programs now and request one as soon as that is done.'}
        </p>
      )}

      <ul className="space-y-4" aria-label="Service programs">
        {bundles.map((bundle) => (
          <li key={bundle.id} className="rounded-lg border border-gray-200 p-4" data-testid="bundle-card">
            <div className="flex items-start justify-between gap-3">
              <h3 className="min-w-0 break-words text-base font-semibold text-gray-900">{bundle.name}</h3>
              <span className="shrink-0 text-xs text-gray-600">{programSpan(bundle.visitCount, bundle.visitSpanDays)}</span>
            </div>
            {bundle.description && <p className="mt-1 break-words text-sm text-gray-700">{bundle.description}</p>}

            <ol className="mt-3 divide-y divide-gray-100 text-sm" aria-label={`${bundle.name} visits`}>
              {bundle.visits.map((visit) => (
                <li key={visit.step} className="flex items-baseline justify-between gap-3 py-2" data-testid="bundle-visit">
                  <span className="min-w-0 break-words">
                    <span className="mr-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-900">{programDayLabel(visit.offsetDays)}</span>
                    {visit.itemName}
                    {Number(visit.quantity) !== 1 && <span className="text-gray-600"> × {visit.quantity}</span>}
                  </span>
                  <span className={`shrink-0 ${visit.estimatedPrice === null ? 'text-xs text-gray-500' : 'font-medium text-gray-900'}`}>
                    {priceOrApproval(visit.estimatedPrice, invoiceMoney)}
                  </span>
                </li>
              ))}
            </ol>
            <p className="mt-2 flex justify-between gap-3 border-t pt-2 text-sm font-semibold text-gray-900" data-testid="bundle-total">
              <span>Estimated total</span>
              <span>{bundle.estimatedTotal === null ? PRICED_ON_APPROVAL : invoiceMoney(bundle.estimatedTotal)}</span>
            </p>
            {bundle.estimatedTotal !== null && Number(bundle.estimatedTax) > 0 && (
              <p className="text-xs text-gray-500">Includes about {invoiceMoney(bundle.estimatedTax ?? '0')} tax. The final prices are confirmed when your request is approved.</p>
            )}

            {canRequest && (
              <div className="mt-3 space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    id={`bundle-autorenew-${bundle.id}`} type="checkbox" className="mt-1 h-5 w-5"
                    checked={autoRenew[bundle.id] ?? bundle.autoRenewDefault} disabled={!!busyId}
                    onChange={(e) => setAutoRenew((current) => ({ ...current, [bundle.id]: e.target.checked }))}
                  />
                  <label htmlFor={`bundle-autorenew-${bundle.id}`} className="text-sm text-gray-800">
                    Auto-renew
                    <span className="block text-xs text-gray-600">
                      Only means we prepare a request for next season when this program ends. {me.companyName} still reviews it, and nothing is approved or charged for you.
                    </span>
                  </label>
                </div>
                {failure?.bundleId === bundle.id && <InlineAlert>{failure.failure.message}</InlineAlert>}
                <button type="button" className={`${primaryButtonClass} w-full sm:w-auto`} disabled={!!busyId} onClick={() => void request(bundle)}>
                  {busyId === bundle.id ? 'Sending…' : `Request ${bundle.name}`}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
